import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Loader2 } from 'lucide-react';

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const errorParam = searchParams.get('error');

  useEffect(() => {
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }
    if (!code || !state) {
      setError('Missing OAuth parameters');
      return;
    }
    handleCallback();
  }, [code, state, errorParam]);

  const handleCallback = async () => {
    try {
      // Try social account linking first (Instagram/Facebook pages)
      const socialRes = await apiClient.post('/social/exchange-token', { code, state }).catch(() => null);
      // A valid social flow always returns a `platform`; branch on success/failure
      if (socialRes?.data?.platform) {
        if (socialRes.data.success) {
          navigate(`/social-accounts?success=true&platform=${socialRes.data.platform}`, { replace: true });
        } else {
          navigate(`/social-accounts?error=${encodeURIComponent(socialRes.data.message || 'Could not connect the account.')}`, { replace: true });
        }
        return;
      }

      // Try Facebook login auth
      const authRes = await apiClient.post('/auth/facebook/exchange', { code, state }).catch(() => null);
      if (authRes?.data?.token) {
        localStorage.setItem('token', authRes.data.token);
        if (authRes.data.user) setUser(authRes.data.user);
        navigate('/dashboard', { replace: true });
        return;
      }

      setError('OAuth callback failed. Please try again.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md" data-testid="callback-error">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="text-violet-400 hover:underline">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="callback-loading">
      <div className="text-center">
        <Loader2 className="h-10 w-10 animate-spin text-violet-500 mx-auto mb-3" />
        <p className="text-slate-400 text-sm">Completing authentication...</p>
      </div>
    </div>
  );
}
